import React, { useEffect, useState, ChangeEvent, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import useCreatePost from '../hooks/useCreatePost';
import useGetCategories from '../hooks/useGetCategories';
import useGetTags from '../hooks/useGetTags';
import styles from './CreatePostPage.module.css';
import { useAuth } from "../hooks/useAuth";

// Интерфейсы для типизации
interface FormImage {
    file: File | null;
    preview: string;
    caption: string;
}

interface Category {
    id: number;
    name: string;
}

interface Tag {
    id: number;
    name: string;
}

interface FormDataState {
    title: string;
    content: string;
    excerpt: string;
    category: string;
    authorId: number;
    tags: number[];
    images: FormImage[];
}

const CreatePostPage = () => {
    const navigate = useNavigate();
    const { createPost, isLoading, error } = useCreatePost();
    const { user } = useAuth();
    const {
        getCategories,
        isLoading: categoriesLoading,
        error: categoriesError,
        data: categoriesData
    } = useGetCategories();
    const {
        getTags,
        isLoading: tagsLoading,
        error: tagsError,
        data: tagsData
    } = useGetTags();

    const [formData, setFormData] = useState<FormDataState>({
        title: '',
        content: '',
        excerpt: '',
        category: '',
        authorId: user?.id || 0,
        tags: [],
        images: []
    });
    const [currentTag, setCurrentTag] = useState<string>('');
    const [currentImage, setCurrentImage] = useState<{ file: File | null; caption: string }>({
        file: null,
        caption: ''
    });

    // Случайные подсказки для заголовка
    const titlePlaceholders = [
        "Например: 'Как я победил баг в 3 часа ночи'",
        "Что-то гениальное и необычное",
        "Заголовок, от которого все ахнут",
        "Название, которое соберет много лайков",
        "О чём бы вы хотели рассказать миру?"
    ];

    const [currentTitlePlaceholder] = useState(
        titlePlaceholders[Math.floor(Math.random() * titlePlaceholders.length)]
    );

    useEffect(() => {
        getCategories();
        getTags();
    }, []);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddTag = () => {
        if (currentTag.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, parseInt(currentTag)]
            }));
            setCurrentTag('');
        }
    };

    const handleRemoveTag = (index: number) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index)
        }));
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        if (file) {
            // Проверяем тип файла
            if (!file.type.startsWith('image/')) {
                alert('Пожалуйста, выберите изображение! 📸');
                return;
            }

            // Проверяем размер файла (максимум 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('Изображение слишком большое! Максимальный размер: 5MB 📏');
                return;
            }

            setCurrentImage(prev => ({ ...prev, file }));
        }
    };

    const handleImageCaptionChange = (e: ChangeEvent<HTMLInputElement>) => {
        setCurrentImage(prev => ({ ...prev, caption: e.target.value }));
    };

    const handleAddImage = () => {
        if (currentImage.file) {
            const imageUrl = URL.createObjectURL(currentImage.file);

            setFormData(prev => ({
                ...prev,
                images: [
                    ...prev.images,
                    {
                        file: currentImage.file,
                        preview: imageUrl,
                        caption: currentImage.caption || "Без названия 🤷‍♂️"
                    }
                ]
            }));

            setCurrentImage({ file: null, caption: '' });

            // Сбрасываем значение input file
            const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
            if (fileInput) {
                fileInput.value = '';
            }
        }
    };

    const handleRemoveImage = (index: number) => {
        // Освобождаем URL объекта
        URL.revokeObjectURL(formData.images[index].preview);

        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Проверяем, что выбрана категория
        if (!formData.category) {
            alert('Пожалуйста, выберите категорию! 📂');
            return;
        }

        try {
            // Подготавливаем данные для отправки
            const postData = {
                ...formData,
                category: parseInt(formData.category),
                images: formData.images.map(img => ({
                    file: img.file,
                    caption: img.caption
                }))
            };
            // @ts-ignore
            const createdPost = await createPost(postData);

            navigate('/post-created', {
                state: {
                    postId: createdPost.id,
                    title: createdPost.title
                }
            });
        } catch (err) {
            console.error('Ошибка при создании поста:', err);
            alert('Ошибка при создании поста. Возможно, недостаточно кофеина в системе ☕');
        }
    };

    // Проверка авторизации пользователя
    if (!user) {
        return (
            <div className={styles.createPostPage}>
                <h1>Создать новый пост</h1>
                <div className={styles.error}>
                    Для создания поста необходимо авторизоваться
                </div>
                <button
                    onClick={() => navigate('/login')}
                    className={styles.loginRedirectButton}
                >
                    Перейти к авторизации
                </button>
            </div>
        );
    }

    return (
        <div className={styles.createPostPage}>
            <div className={styles.headerSection}>
                <h1>Создать новый пост</h1>
                <p className={styles.subtitle}>Поделитесь своей гениальной идеей с миром! ✨</p>
            </div>

            {error && (
                <div className={styles.error}>
                    ⚠️ Ошибка: {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.postForm}>
                <div className={styles.formGroup}>
                    <label htmlFor="title">Заголовок *</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder={currentTitlePlaceholder}
                        required
                        disabled={isLoading}
                        maxLength={100}
                    />
                    <div className={styles.charCount}>
                        {formData.title.length}/100 символов
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="content">Содержание *</label>
                    <textarea
                        id="content"
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        rows={8}
                        placeholder="Расскажите свою историю... Возможно, она изменит чью-то жизнь 🎯"
                        required
                        disabled={isLoading}
                    />
                    <div className={styles.charCount}>
                        {formData.content.length} символов {formData.content.length > 1000 ? '🔥' : '📝'}
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="excerpt">Краткое описание</label>
                    <textarea
                        id="excerpt"
                        name="excerpt"
                        value={formData.excerpt}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Краткое описание, которое заинтересует читателей..."
                        disabled={isLoading}
                        maxLength={200}
                    />
                    <div className={styles.charCount}>
                        {formData.excerpt.length}/200 символов
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label htmlFor="category">Категория *</label>
                    <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleInputChange}
                        disabled={isLoading || categoriesLoading}
                        required
                    >
                        <option value="">Выберите категорию 🗂️</option>
                        {categoriesData?.map((category: Category) => (
                            <option key={category.id} value={category.id.toString()}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    {categoriesLoading && <div className={styles.loading}>Загрузка категорий...</div>}
                    {categoriesError && (
                        <div className={styles.error}>
                            Ошибка загрузки категорий: {categoriesError}
                        </div>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label>Теги</label>
                    <div className={styles.tagsInput}>
                        <select
                            value={currentTag}
                            onChange={(e) => setCurrentTag(e.target.value)}
                            disabled={isLoading || tagsLoading}
                        >
                            <option value="">Выберите тег 🏷️</option>
                            {tagsData?.map((tag: Tag) => (
                                <option key={tag.id} value={tag.id.toString()}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={handleAddTag}
                            className={styles.addButton}
                            disabled={isLoading || tagsLoading || !currentTag}
                        >
                            Добавить тег
                        </button>
                    </div>

                    {tagsLoading && <div className={styles.loading}>Загрузка тегов...</div>}
                    {tagsError && (
                        <div className={styles.error}>
                            Ошибка загрузки тегов: {tagsError}
                        </div>
                    )}

                    {formData.tags.length > 0 && (
                        <div className={styles.tagsList}>
                            <p>Добавленные теги:</p>
                            {formData.tags.map((tagId, index) => {
                                const tag = tagsData?.find((t: Tag) => t.id === tagId);
                                return (
                                    <span key={index} className={styles.tag}>
                                        {tag ? tag.name : `#${tagId}`}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(index)}
                                            className={styles.removeTagButton}
                                            disabled={isLoading}
                                            title="Удалить тег"
                                        >
                                            ×
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.formGroup}>
                    <label>Изображения 📸</label>
                    <div className={styles.uploadHint}>
                        Максимальный размер: 5MB. Поддерживаемые форматы: JPG, PNG, GIF
                    </div>

                    <div className={styles.imageInput}>
                        <div className={styles.fileInput}>
                            <label htmlFor="image-upload" className={styles.fileInputLabel}>
                                {currentImage.file ? 'Файл выбран ✅' : 'Выберите файл'}
                            </label>
                            <input
                                id="image-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                disabled={isLoading}
                            />
                        </div>

                        <input
                            type="text"
                            placeholder="Подпись к изображению (необязательно)"
                            value={currentImage.caption}
                            onChange={handleImageCaptionChange}
                            disabled={isLoading}
                        />

                        <button
                            type="button"
                            onClick={handleAddImage}
                            disabled={!currentImage.file || isLoading}
                            className={styles.addButton}
                        >
                            Добавить изображение
                        </button>
                    </div>

                    {formData.images.length > 0 && (
                        <div className={styles.imagesList}>
                            <p>Добавленные изображения ({formData.images.length}):</p>
                            {formData.images.map((image, index) => (
                                <div key={index} className={styles.imageItem}>
                                    <img src={image.preview} alt={image.caption}/>
                                    <p>{image.caption}</p>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveImage(index)}
                                        className={styles.removeButton}
                                        disabled={isLoading}
                                        title="Удалить изображение"
                                    >
                                        Удалить
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={() => navigate('/')}
                        className={styles.cancelButton}
                        disabled={isLoading}
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isLoading || !formData.title || !formData.content || !formData.category}
                    >
                        {isLoading ? (
                            <>
                                <span className={styles.spinner}></span>
                                Публикуем...
                            </>
                        ) : (
                            'Опубликовать 🚀'
                        )}
                    </button>
                </div>
            </form>

            <div className={styles.tips}>
                <h3>Советы по созданию отличного поста:</h3>
                <ul>
                    <li>💡 Пишите о том, что действительно интересно вам</li>
                    <li>🎯 Будьте конкретны в заголовке</li>
                    <li>📝 Добавьте код или примеры, если уместно</li>
                    <li>😊 Не забывайте про доброту и уважение к читателям</li>
                </ul>
            </div>
        </div>
    );
};

export default CreatePostPage;